import { categoryIndicators, mapRiskName, type RiskIndicator } from "../lib/risk-contracts";
import {
  applyExpectedCloseV2DealContributions,
  type ExpectedCloseV2CompanyForecast,
  type ExpectedCloseV2DealForecast,
} from "../expected-close-v2/localExpectedCloseForecast";
import { buildForecastCurrentSignalEvidence } from "../state-of-sales-v2/forecastInsights";
import { FORECAST_MANAGEMENT_ATTENTION_CONFIG } from "../state-of-sales-v2/forecastManagementAttention";
import { getStateOfSalesV2ForecastPosition } from "../state-of-sales-v2/forecastPosition";
import type { useStateOfSalesV2Forecast } from "../state-of-sales-v2/useForecastData";
import { REVENUE_INTELLIGENCE_COPY } from "../lib/revenueIntelligenceCopy";
import {
  getForecastAgreementLabel,
  getForecastAgreementScore,
  type ForecastDiagnosisDeal,
  type ForecastDiagnosisDriver,
  type ForecastGapGroup,
  type ForecastDiagnosisScenario,
  type ForecastDiagnosisScenarioCondition,
  type ForecastDiagnosisStory,
} from "./forecastDiagnosisStory";

type ForecastModel = ReturnType<typeof useStateOfSalesV2Forecast>;
type ForecastEvaluation = NonNullable<ForecastModel["forecast"]>;
type PreparedDeal = ForecastModel["filteredPreparedDeals"][number];
type EvaluatedOpenDeal = ForecastEvaluation["stateOfSalesV2OpenDeals"][number];
type DiagnosisOpenDeal = PreparedDeal & Partial<EvaluatedOpenDeal>;

const TIMING_AND_VELOCITY_INDICATORS: RiskIndicator[] = [
  "stageProgression",
  "closeDateInPast",
  "closeDateSlippage",
  "stageRegression",
];
const PIPELINE_MOMENTUM_INDICATORS: RiskIndicator[] = [
  "lastModifiedDate",
  "emailResponseTime",
];

const DRIVER_DEFAULT_VISIBLE_DEAL_COUNT = 5;
const DRIVER_MATERIAL_RELATIVE_DIFFERENCE = 0.15;
const DRIVER_MATERIAL_FORECAST_SHARE = 0.005;
const GAP_DEFAULT_VISIBLE_DEAL_COUNT = 5;
const GAP_MATERIAL_RELATIVE_DIFFERENCE = 0.3;
const GAP_MATERIAL_FORECAST_SHARE = 0.005;

type DriverDealImpact = {
  comparisonValue: number;
  id: string;
  impact: number;
};

export function selectForecastDriverDeals(
  rows: DriverDealImpact[],
  forecastReferenceValue: number,
) {
  const sortedRows = rows.toSorted((a, b) => b.impact - a.impact);
  const allDealIds = sortedRows.map(row => row.id);
  if (sortedRows.length <= DRIVER_DEFAULT_VISIBLE_DEAL_COUNT) {
    return { allDealIds, materialDealIds: allDealIds };
  }

  const selectedIds = new Set(
    sortedRows.slice(0, DRIVER_DEFAULT_VISIBLE_DEAL_COUNT).map(row => row.id),
  );
  const minimumForecastImpact = Math.max(0, forecastReferenceValue)
    * DRIVER_MATERIAL_FORECAST_SHARE;

  for (const row of sortedRows) {
    const relativeDifference = row.comparisonValue > 0
      ? row.impact / row.comparisonValue
      : 0;
    if (
      relativeDifference >= DRIVER_MATERIAL_RELATIVE_DIFFERENCE
      && row.impact >= minimumForecastImpact
    ) {
      selectedIds.add(row.id);
    }
  }

  return {
    allDealIds,
    materialDealIds: allDealIds.filter(id => selectedIds.has(id)),
  };
}

type GapDealImpact = {
  baselineContribution: number;
  evidenceContribution: number;
  gapContribution: number;
  id: string;
};

export function selectForecastGapDeals(
  rows: GapDealImpact[],
  forecastReferenceValue: number,
) {
  const sortedRows = rows.toSorted((a, b) => b.gapContribution - a.gapContribution);
  if (sortedRows.length <= GAP_DEFAULT_VISIBLE_DEAL_COUNT) return sortedRows.map(row => row.id);

  const selectedIds = new Set(
    sortedRows
      .slice(0, GAP_DEFAULT_VISIBLE_DEAL_COUNT)
      .map(row => row.id),
  );
  const minimumForecastImpact = Math.max(0, forecastReferenceValue)
    * GAP_MATERIAL_FORECAST_SHARE;

  for (const row of sortedRows) {
    const comparisonValue = Math.max(
      Math.abs(row.baselineContribution),
      Math.abs(row.evidenceContribution),
    );
    const relativeDifference = comparisonValue > 0
      ? row.gapContribution / comparisonValue
      : 0;
    if (
      relativeDifference >= GAP_MATERIAL_RELATIVE_DIFFERENCE
      && row.gapContribution >= minimumForecastImpact
    ) {
      selectedIds.add(row.id);
    }
  }

  return sortedRows.filter(row => selectedIds.has(row.id)).map(row => row.id);
}

export type CompatibleCrmForecastSubmission = {
  amount: number;
  asOf: string;
  scopeMatch: "exact" | "mismatch";
  source: "hubspot";
};

export type ForecastDiagnosisBuildInput = {
  accumulated: boolean;
  chartData?: ForecastModel["chartData"];
  companyCurrency: ForecastModel["companyCurrency"];
  crmSubmission?: CompatibleCrmForecastSubmission | null;
  expectedCloseV2CompanyForecast?: ExpectedCloseV2CompanyForecast;
  filteredPreparedDeals: PreparedDeal[];
  forecast: ForecastEvaluation;
  period: ForecastModel["period"];
};

const periodLabels: Record<ForecastModel["period"], string> = {
  this_month: "This month",
  next_month: "Next month",
  prev_quarter: "Last quarter",
  this_quarter: "This quarter",
  next_quarter: "Next quarter",
  next_6_months: "Next 6 months",
  this_year: "This year",
};

const periodConditionLabels: Record<ForecastModel["period"], string> = {
  this_month: "this month",
  next_month: "next month",
  prev_quarter: "last quarter",
  this_quarter: "this quarter",
  next_quarter: "next quarter",
  next_6_months: "the next 6 months",
  this_year: "this year",
};

function toFiniteNumber(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function toSentence(value: string) {
  const trimmed = value.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function isDateInRange(value: unknown, from: Date, to: Date) {
  const date = value instanceof Date ? value : new Date(String(value ?? ""));
  return Number.isFinite(date.getTime()) && date >= from && date <= to;
}

function getMonthKey(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
}

export function getSelectedPeriodMonths(from: Date, to: Date) {
  const months = new Set<string>();
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cursor <= end) {
    months.add(getMonthKey(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

export function getExpectedCloseV2Timing(
  forecast: ExpectedCloseV2DealForecast | undefined,
  selectedMonths: ReadonlySet<string>,
  selectedPeriodEndMonth: string,
) {
  if (!forecast) return null;
  const probabilities = forecast.wonProbabilityByMonth
    .map(([month, probability]) => ({
      month,
      probability: Math.max(0, Math.min(1, toFiniteNumber(probability))),
    }))
    .filter(row => /^\d{4}-\d{2}$/.test(row.month) && row.probability > 0)
    .toSorted((a, b) => a.month.localeCompare(b.month));
  const totalWonProbability = probabilities.reduce((sum, row) => sum + row.probability, 0);
  if (totalWonProbability <= 0) return null;

  const inPeriodProbability = probabilities.reduce(
    (sum, row) => selectedMonths.has(row.month) ? sum + row.probability : sum,
    0,
  );
  const afterPeriodProbability = probabilities.reduce(
    (sum, row) => row.month > selectedPeriodEndMonth ? sum + row.probability : sum,
    0,
  );
  let cumulativeProbability = 0;
  const expectedCloseMonth = probabilities.find((row) => {
    cumulativeProbability += row.probability;
    return cumulativeProbability >= totalWonProbability / 2;
  })?.month ?? null;

  return {
    afterPeriodProbability,
    expectedAfterPeriod: Boolean(
      expectedCloseMonth
      && expectedCloseMonth > selectedPeriodEndMonth
      && afterPeriodProbability > inPeriodProbability
    ),
    expectedCloseMonth,
    inPeriodProbability,
  };
}

function formatExpectedCloseMonth(value: string | null) {
  if (!value) return null;
  const date = new Date(`${value}-01T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  }).format(date);
}

function isDateWithinDaysAfter(value: unknown, from: Date, days: number) {
  const date = value instanceof Date ? value : new Date(String(value ?? ""));
  if (!Number.isFinite(date.getTime())) return false;
  const maximum = new Date(from);
  maximum.setDate(maximum.getDate() + days);
  return date > from && date <= maximum;
}

function toIsoDate(value: unknown) {
  const date = value instanceof Date ? value : new Date(String(value ?? ""));
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function formatShortDate(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(date);
}

function getProviderProperties(deal: PreparedDeal | Record<string, unknown>) {
  const providerData = (deal as { provider_data?: unknown }).provider_data;
  if (!providerData || typeof providerData !== "object") return {} as Record<string, unknown>;
  const properties = (providerData as { properties?: unknown }).properties;
  return properties && typeof properties === "object"
    ? properties as Record<string, unknown>
    : {} as Record<string, unknown>;
}

function humanizeCategory(value: string) {
  return value
    .replace(/^forecast_category_/i, "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());
}

export function getOptionalForecastCategory(deal: PreparedDeal | Record<string, unknown>) {
  const properties = getProviderProperties(deal);
  const raw = [
    properties.hs_forecast_category,
    properties.hs_manual_forecast_category,
    properties.forecast_category,
  ].find(value => typeof value === "string" && value.trim());
  return typeof raw === "string" ? humanizeCategory(raw.trim()) : null;
}

function formatMoney(value: number, currencyCode: string) {
  const absolute = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (absolute >= 1_000_000) return `${sign}${(absolute / 1_000_000).toFixed(1)} M${currencyCode}`;
  if (absolute >= 1_000) return `${sign}${(absolute / 1_000).toFixed(1)} k${currencyCode}`;
  return `${sign}${Math.round(absolute)} ${currencyCode}`;
}

export function getCrmStageWeightedForecast(
  forecast: Pick<ForecastEvaluation, "forecastChartData">,
  period: ForecastModel["period"],
  accumulated: boolean,
) {
  const hasLeadingContextMonth = period === "this_year" || period === "next_6_months";
  const leadingContext = hasLeadingContextMonth ? forecast.forecastChartData.at(0) : undefined;
  const periodRows = hasLeadingContextMonth
    ? forecast.forecastChartData.slice(1)
    : forecast.forecastChartData;
  if (accumulated) {
    return Math.round(
      toFiniteNumber(periodRows.at(-1)?.crm_forecast)
      - toFiniteNumber(leadingContext?.crm_forecast),
    );
  }
  return Math.round(periodRows.reduce(
    (sum, row) => sum + toFiniteNumber(row.crm_forecast),
    0,
  ));
}

export function getTopRiskEvidence(
  deal: Record<string, unknown>,
  indicators: RiskIndicator[] = categoryIndicators.forecast,
) {
  const risks = deal.risks && typeof deal.risks === "object"
    ? deal.risks as Partial<Record<RiskIndicator, { risk?: unknown; risk_explanation?: unknown }>>
    : {};
  return indicators
    .map(indicator => {
      const evidence = risks[indicator];
      return {
        explanation: typeof evidence?.risk_explanation === "string" && evidence.risk_explanation.trim()
          ? evidence.risk_explanation.trim()
          : null,
        indicator,
        score: toFiniteNumber(evidence?.risk),
      };
    })
    .toSorted((a, b) => b.score - a.score)
    .at(0) ?? null;
}

function getDealSignal(
  deal: Record<string, unknown>,
  indicators: RiskIndicator[] = categoryIndicators.forecast,
) {
  const evidence = getTopRiskEvidence(deal, indicators);
  if (!evidence || evidence.score <= 0) return "No material forecast risk signal is currently identified.";
  return evidence.explanation
    ? `${mapRiskName(evidence.indicator)}: ${evidence.explanation}`
    : `${mapRiskName(evidence.indicator)} is the strongest current forecast signal.`;
}

function getMovementDealIds(forecast: ForecastEvaluation, keys: Array<"moved_out" | "weakened_outlook">) {
  const movement = forecast.stateOfSalesV2ForecastMovement;
  if (!movement?.available) return new Set<string>();
  return new Set(keys.flatMap(key => movement.dealsByCategory[key].map(deal => String(deal.id))));
}

function buildDealRows(
  forecast: ForecastEvaluation,
  preparedDeals: PreparedDeal[],
  evaluatedOpenDeals: readonly DiagnosisOpenDeal[],
  expectedCloseV2CompanyForecast?: ExpectedCloseV2CompanyForecast,
) {
  const range = forecast.stateOfSalesV2SelectedPeriodRange;
  const evaluatedById = new Map(
    evaluatedOpenDeals.map(deal => [String(deal.id), deal]),
  );
  const expectedCloseV2ByDealId = new Map(
    (expectedCloseV2CompanyForecast?.dealForecasts ?? []).map(deal => [String(deal.dealId), deal]),
  );
  const terminalBuyerOutcomeByDealId = new Map(
    (expectedCloseV2CompanyForecast?.terminalBuyerOutcomeDeals ?? [])
      .map(outcome => [String(outcome.dealId), outcome]),
  );
  const selectedMonths = getSelectedPeriodMonths(range.from, range.to);
  const selectedPeriodEndMonth = getMonthKey(range.to);
  const uniquePreparedDeals = Array.from(
    new Map(preparedDeals.map(deal => [String(deal.id), deal])).values(),
  );

  return uniquePreparedDeals.flatMap(preparedDeal => {
    const id = String(preparedDeal.id);
    const evaluated = evaluatedById.get(id);
    const isOpen = !preparedDeal.is_closed && !preparedDeal.is_won && !preparedDeal.is_lost;
    if (!isOpen) return [];

    const crmCloseInPeriod = isDateInRange(preparedDeal.closed_at, range.from, range.to);
    const evidenceCloseInPeriod = evaluated !== undefined;
    const nearTermOutside = isDateWithinDaysAfter(
      preparedDeal.adjustedCloseDate,
      range.to,
      30,
    );
    const terminalBuyerOutcome = terminalBuyerOutcomeByDealId.get(id);
    const closeTiming = terminalBuyerOutcome
      ? null
      : getExpectedCloseV2Timing(
        expectedCloseV2ByDealId.get(id),
        selectedMonths,
        selectedPeriodEndMonth,
      );
    if (
      !crmCloseInPeriod
      && !evidenceCloseInPeriod
      && !nearTermOutside
      && !closeTiming?.expectedAfterPeriod
    ) return [];

    const sourceDeal = (evaluated ?? preparedDeal) as Record<string, unknown>;
    const fullValue = Math.round(toFiniteNumber(preparedDeal.value));
    const baselineContribution = crmCloseInPeriod
      ? Math.round(fullValue * toFiniteNumber(preparedDeal.stage_probability))
      : 0;
    const evidenceContribution = evidenceCloseInPeriod
      ? Math.round(toFiniteNumber(evaluated.forecast_contribution))
      : 0;
    const expectedCloseMonth = formatExpectedCloseMonth(closeTiming?.expectedCloseMonth ?? null);
    const closeTimingSignal = terminalBuyerOutcome
      ? "The buyer has explicitly ended the evaluation."
      : closeTiming?.expectedAfterPeriod
        ? expectedCloseMonth
          ? `Revelate expects this Deal to close in ${expectedCloseMonth}, after the period.`
          : "Revelate expects this Deal to close after the period."
        : "Revelate does not currently expect this Deal to move beyond the period.";

    return [{
      adjustedCloseDate: toIsoDate(preparedDeal.adjustedCloseDate),
      baselineContribution,
      driverSignals: {
        "close-timing": closeTimingSignal,
        "pipeline-momentum": getDealSignal(sourceDeal, PIPELINE_MOMENTUM_INDICATORS),
        "timing-and-deal-velocity": getDealSignal(sourceDeal, TIMING_AND_VELOCITY_INDICATORS),
      },
      evidenceCloseInPeriod,
      evidenceContribution,
      forecastCategory: getOptionalForecastCategory(preparedDeal),
      fullValue,
      id,
      name: String(preparedDeal.name ?? "Unnamed opportunity"),
      nextStep: typeof preparedDeal.next_step === "string" && preparedDeal.next_step.trim()
        ? preparedDeal.next_step.trim()
        : null,
      nextStepUpdatedAt: toIsoDate(preparedDeal.next_step_updated_at),
      riskScore: toFiniteNumber((evaluated as { risk_score?: unknown } | undefined)?.risk_score),
      signal: terminalBuyerOutcome
        ? `Buyer explicitly ended the evaluation: “${terminalBuyerOutcome.evidence}”`
        : getDealSignal(sourceDeal),
      stage: String(preparedDeal.stage_label ?? "Unknown stage"),
      stageProbability: toFiniteNumber(preparedDeal.stage_probability_adjusted),
      upsideDelta: Math.max(0, fullValue - evidenceContribution),
      upsideEligible: !terminalBuyerOutcome
        && (evidenceCloseInPeriod || nearTermOutside || Boolean(closeTiming?.expectedAfterPeriod)),
      closeTiming: closeTiming
        ? {
          ...closeTiming,
          expectedCloseMonth,
        }
        : null,
      crmCloseDate: toIsoDate(preparedDeal.closed_at),
    }];
  });
}

type DiagnosisDealRow = ReturnType<typeof buildDealRows>[number];

function buildForecastGapBreakdown(
  dealRows: DiagnosisDealRow[],
  netGap: number,
  forecastReferenceValue: number,
  usesStageWeightedBaseline: boolean,
) {
  if (!usesStageWeightedBaseline || netGap === 0) return null;

  const groups = new Map<ForecastGapGroup["id"], ForecastGapGroup>();
  const definitions: Record<ForecastGapGroup["id"], Pick<ForecastGapGroup, "direction" | "label" | "summary">> = {
    "moved-out-of-period": {
      direction: "below",
      label: REVENUE_INTELLIGENCE_COPY.forecast.movedOutOfPeriod,
      summary: REVENUE_INTELLIGENCE_COPY.forecast.movedOutOfPeriodSummary,
    },
    "crm-overstates-value": {
      direction: "below",
      label: REVENUE_INTELLIGENCE_COPY.forecast.crmOverstatesValue,
      summary: REVENUE_INTELLIGENCE_COPY.forecast.crmOverstatesValueSummary,
    },
    "revelate-above-crm": {
      direction: "above",
      label: REVENUE_INTELLIGENCE_COPY.forecast.crmUnderstatesValue,
      summary: REVENUE_INTELLIGENCE_COPY.forecast.crmUnderstatesValueSummary,
    },
  };

  for (const deal of dealRows) {
    const signedGap = Math.round(deal.baselineContribution - deal.evidenceContribution);
    if (signedGap === 0) continue;

    const id: ForecastGapGroup["id"] = signedGap < 0
      ? "revelate-above-crm"
      : deal.closeTiming?.expectedAfterPeriod
        ? "moved-out-of-period"
        : "crm-overstates-value";
    const definition = definitions[id];
    const group = groups.get(id) ?? {
      dealIds: [],
      deals: [],
      direction: definition.direction,
      fullDealValue: 0,
      gapContribution: 0,
      id,
      label: definition.label,
      summary: definition.summary,
    };
    const gapContribution = Math.abs(signedGap);
    group.dealIds.push(deal.id);
    group.deals.push({
      baselineContribution: deal.baselineContribution,
      crmCloseDate: deal.crmCloseDate,
      evidenceContribution: deal.evidenceContribution,
      fullValue: deal.fullValue,
      gapContribution,
      id: deal.id,
      name: deal.name,
      revelateClose: deal.closeTiming?.expectedCloseMonth ?? null,
      stage: deal.stage,
    });
    group.fullDealValue += deal.fullValue;
    group.gapContribution += gapContribution;
    groups.set(id, group);
  }

  const order: ForecastGapGroup["id"][] = [
    "moved-out-of-period",
    "crm-overstates-value",
    "revelate-above-crm",
  ];
  const visibleGroups = order
    .map(id => groups.get(id) ?? {
      dealIds: [],
      deals: [],
      direction: definitions[id].direction,
      fullDealValue: 0,
      gapContribution: 0,
      id,
      label: definitions[id].label,
      summary: definitions[id].summary,
    })
    .map(group => {
      const sortedDeals = group.deals.toSorted((a, b) => group.id === "moved-out-of-period"
        ? b.fullValue - a.fullValue
        : b.gapContribution - a.gapContribution);
      const selectedIds = new Set(selectForecastGapDeals(sortedDeals, forecastReferenceValue));
      const relevantDeals = sortedDeals.filter(deal => selectedIds.has(deal.id));
      return {
        ...group,
        dealIds: relevantDeals.map(deal => deal.id),
        deals: relevantDeals,
        fullDealValue: relevantDeals.reduce((sum, deal) => sum + deal.fullValue, 0),
        gapContribution: relevantDeals.reduce((sum, deal) => sum + deal.gapContribution, 0),
      };
    });

  return { groups: visibleGroups, netGap };
}

function toPublicDeal(deal: DiagnosisDealRow): ForecastDiagnosisDeal {
  return {
    adjustedCloseDate: deal.adjustedCloseDate,
    baselineContribution: deal.baselineContribution,
    driverSignals: deal.driverSignals,
    evidenceCloseInPeriod: deal.evidenceCloseInPeriod,
    evidenceContribution: deal.evidenceContribution,
    forecastCategory: deal.forecastCategory,
    fullValue: deal.fullValue,
    id: deal.id,
    name: deal.name,
    nextStep: deal.nextStep,
    nextStepUpdatedAt: deal.nextStepUpdatedAt,
    signal: deal.signal,
    stage: deal.stage,
    upsideDelta: deal.upsideDelta,
  };
}

type ScenarioEventType = "lost" | "moved-out" | "moved-in" | "won";

type ScenarioCandidate = {
  deal: DiagnosisDealRow;
  eventType: ScenarioEventType;
  impact: number;
  score: number;
};

const SCENARIO_DEFAULT_DEAL_COUNT = 2;
const SCENARIO_MAX_DEAL_COUNT = 10;
const SCENARIO_MATERIAL_FORECAST_SHARE = 0.05;
const SCENARIO_EVENT_MINIMUM_FORECAST_SHARE = 0.005;

function selectScenarioCandidates(
  candidates: ScenarioCandidate[],
  expectedOutcome: number,
) {
  const sortedCandidates = candidates.toSorted((a, b) => b.score - a.score);
  if (sortedCandidates.length <= SCENARIO_DEFAULT_DEAL_COUNT) return sortedCandidates;

  const selectedByDealId = new Map<string, ScenarioCandidate>();
  const minimumEventImpact = Math.max(0, expectedOutcome)
    * SCENARIO_EVENT_MINIMUM_FORECAST_SHARE;
  const materialScenarioImpact = Math.max(0, expectedOutcome)
    * SCENARIO_MATERIAL_FORECAST_SHARE;

  for (const eventType of new Set(sortedCandidates.map(candidate => candidate.eventType))) {
    const strongestForEvent = sortedCandidates.find(candidate => candidate.eventType === eventType);
    if (strongestForEvent && strongestForEvent.impact >= minimumEventImpact) {
      selectedByDealId.set(strongestForEvent.deal.id, strongestForEvent);
    }
  }

  for (const candidate of sortedCandidates) {
    const selectedImpact = Array.from(selectedByDealId.values()).reduce(
      (sum, selected) => sum + selected.impact,
      0,
    );
    if (
      selectedByDealId.size >= SCENARIO_DEFAULT_DEAL_COUNT
      && selectedImpact >= materialScenarioImpact
    ) break;
    if (selectedByDealId.size >= SCENARIO_MAX_DEAL_COUNT) break;
    selectedByDealId.set(candidate.deal.id, candidate);
  }

  return sortedCandidates.filter(candidate => selectedByDealId.has(candidate.deal.id));
}

function buildIndividualScenarioCondition(
  candidate: ScenarioCandidate,
  currencyCode: string,
  periodLabel: string,
): ForecastDiagnosisScenarioCondition {
  const { deal, eventType, impact } = candidate;
  const expectedCloseMonth = deal.closeTiming?.expectedCloseMonth;
  const adjustedCloseDate = formatShortDate(deal.adjustedCloseDate);
  const reason = eventType === "moved-out"
    ? expectedCloseMonth
      ? `Revelate expects this Deal to close in ${expectedCloseMonth}, after the period.`
      : "Revelate expects this Deal to close after the period."
    : eventType === "won" && !deal.evidenceCloseInPeriod
      ? adjustedCloseDate
        ? `Revelate expects this Deal to close on ${adjustedCloseDate}, after the period.`
        : "Revelate expects this Deal to close after the period."
      : toSentence(deal.signal);

  const condition = eventType === "lost"
    ? REVENUE_INTELLIGENCE_COPY.forecast.scenario.downsideLost
    : eventType === "moved-out"
      ? REVENUE_INTELLIGENCE_COPY.forecast.scenario.downsideMovedOut
      : eventType === "moved-in"
        ? REVENUE_INTELLIGENCE_COPY.forecast.scenario.upsideMovedIn(periodLabel)
        : REVENUE_INTELLIGENCE_COPY.forecast.scenario.upsideWon(periodLabel);
  const valueExplanation = eventType === "lost" || eventType === "moved-out"
    ? `${reason} The Revelate forecast includes ${formatMoney(impact, currencyCode)} from this Deal. This case removes it.`
    : eventType === "moved-in"
      ? `The Revelate forecast places ${formatMoney(impact, currencyCode)} from this Deal after the period. Moving it into ${periodLabel} adds that value.`
      : deal.evidenceContribution > 0
        ? `${reason} The Revelate forecast includes ${formatMoney(deal.evidenceContribution, currencyCode)} from this Deal. Winning it ${periodLabel} would add ${formatMoney(impact, currencyCode)}.`
        : `${reason} The Revelate forecast excludes this Deal. Winning it ${periodLabel} would add ${formatMoney(impact, currencyCode)}.`;

  return {
    condition,
    impact: eventType === "lost" || eventType === "moved-out" ? -impact : impact,
    impactCondition: REVENUE_INTELLIGENCE_COPY.forecast.scenario.impactCondition(eventType, periodLabel),
    dealCount: 1,
    dealId: deal.id,
    dealIds: [deal.id],
    dealName: deal.name,
    eventType,
    stage: deal.stage,
    valueExplanation,
  };
}

function buildGroupedScenarioConditions(
  candidates: ScenarioCandidate[],
  currencyCode: string,
  periodLabel: string,
): ForecastDiagnosisScenarioCondition[] {
  if (candidates.length <= SCENARIO_DEFAULT_DEAL_COUNT) {
    return candidates.map(candidate => buildIndividualScenarioCondition(
      candidate,
      currencyCode,
      periodLabel,
    ));
  }

  const byEvent = new Map<ScenarioEventType, ScenarioCandidate[]>();
  for (const candidate of candidates) {
    byEvent.set(candidate.eventType, [
      ...(byEvent.get(candidate.eventType) ?? []),
      candidate,
    ]);
  }

  const orderedEvents = Array.from(byEvent.entries())
    .toSorted(([, left], [, right]) => (
      right.reduce((sum, candidate) => sum + candidate.impact, 0)
      - left.reduce((sum, candidate) => sum + candidate.impact, 0)
    ));
  const conditions: ForecastDiagnosisScenarioCondition[] = [];
  for (const [eventType, eventCandidates] of orderedEvents) {
    if (eventCandidates.length === 1) {
      conditions.push(buildIndividualScenarioCondition(
        eventCandidates[0],
        currencyCode,
        periodLabel,
      ));
      continue;
    }

    const dealIds = eventCandidates.map(candidate => candidate.deal.id);
    const impact = Math.round(eventCandidates.reduce(
      (sum, candidate) => sum + candidate.impact,
      0,
    ));
    const condition = eventType === "lost"
      ? REVENUE_INTELLIGENCE_COPY.forecast.scenario.downsideLostGroup(eventCandidates.length)
      : eventType === "moved-out"
        ? REVENUE_INTELLIGENCE_COPY.forecast.scenario.downsideMovedOutGroup(eventCandidates.length, periodLabel)
        : eventType === "moved-in"
          ? REVENUE_INTELLIGENCE_COPY.forecast.scenario.upsideMovedInGroup(eventCandidates.length, periodLabel)
          : REVENUE_INTELLIGENCE_COPY.forecast.scenario.upsideWonGroup(eventCandidates.length, periodLabel);
    const valueExplanation = eventType === "lost" || eventType === "moved-out"
      ? `The Revelate forecast includes ${formatMoney(impact, currencyCode)} from these Deals. This case removes it.`
      : eventType === "moved-in"
        ? `${formatMoney(impact, currencyCode)} from these Deals is currently expected after the period. Moving them into ${periodLabel} adds that value.`
        : `Winning these Deals ${periodLabel} adds ${formatMoney(impact, currencyCode)} beyond the Revelate forecast.`;

    conditions.push({
      condition,
      impact: eventType === "lost" || eventType === "moved-out" ? -impact : impact,
      impactCondition: REVENUE_INTELLIGENCE_COPY.forecast.scenario.impactCondition(eventType, periodLabel),
      dealCount: eventCandidates.length,
      dealId: null,
      dealIds,
      dealName: null,
      eventType,
      stage: null,
      valueExplanation,
    });
  }
  return conditions;
}

function buildScenarios(
  expectedOutcome: number,
  dealRows: DiagnosisDealRow[],
  weakenedDealIds: Set<string>,
  currencyCode: string,
  periodLabel: string,
): ForecastDiagnosisScenario[] {
  const downsideCandidates = selectScenarioCandidates(dealRows
    .filter(deal => deal.evidenceContribution > 0)
    .filter(deal => deal.riskScore >= 25 || weakenedDealIds.has(deal.id))
    .map(deal => ({
      deal,
      eventType: deal.closeTiming?.expectedAfterPeriod
        ? "moved-out" as const
        : "lost" as const,
      impact: deal.evidenceContribution,
      score: deal.evidenceContribution * (1 + deal.riskScore / 100),
    })), expectedOutcome);
  const downsideDelta = -Math.round(downsideCandidates.reduce(
    (sum, candidate) => sum + candidate.impact,
    0,
  ));

  const upsidePool: ScenarioCandidate[] = [];
  for (const deal of dealRows) {
    if (!deal.upsideEligible) continue;
    if (deal.closeTiming?.expectedAfterPeriod) {
      const impact = Math.round(deal.fullValue * deal.closeTiming.afterPeriodProbability);
      if (impact <= 0) continue;
      upsidePool.push({
        deal,
        eventType: "moved-in",
        impact,
        score: impact * (0.5 + deal.stageProbability),
      });
      continue;
    }
    if (deal.upsideDelta <= 0) continue;
    upsidePool.push({
      deal,
      eventType: "won",
      impact: deal.upsideDelta,
      score: deal.upsideDelta * (0.5 + deal.stageProbability),
    });
  }
  const upsideCandidates = selectScenarioCandidates(upsidePool, expectedOutcome);
  const upsideDelta = Math.round(upsideCandidates.reduce(
    (sum, candidate) => sum + candidate.impact,
    0,
  ));

  const downsideConditions = downsideCandidates.length
    ? buildGroupedScenarioConditions(downsideCandidates, currencyCode, periodLabel)
    : [{
      condition: "No material deal-level downside is currently supported by the available evidence.",
      dealId: null,
      dealName: null,
      stage: null,
      valueExplanation: null,
    }];
  const upsideConditions = upsideCandidates.length
    ? buildGroupedScenarioConditions(upsideCandidates, currencyCode, periodLabel)
    : [{
      condition: "No material deal-level upside is currently supported by the available evidence.",
      dealId: null,
      dealName: null,
      stage: null,
      valueExplanation: null,
    }];

  return [
    {
      conditions: [{
        condition: "Current Deal signals and expected close dates hold.",
        dealId: null,
        dealName: null,
        stage: null,
        valueExplanation: null,
      }],
      dealIds: [],
      delta: 0,
      kind: "expected",
      label: REVENUE_INTELLIGENCE_COPY.forecast.revelateForecast,
      summary: "Current reportable forecast",
      value: expectedOutcome,
    },
    {
      conditions: upsideConditions,
      dealIds: upsideCandidates.map(candidate => candidate.deal.id),
      delta: upsideDelta,
      kind: "upside",
      label: "Upside case",
      summary: upsideDelta > 0 ? "If the strongest opportunities close" : "No evidenced upside identified",
      value: expectedOutcome + upsideDelta,
    },
    {
      conditions: downsideConditions,
      dealIds: downsideCandidates.map(candidate => candidate.deal.id),
      delta: downsideDelta,
      kind: "downside",
      label: "Downside case",
      summary: downsideDelta < 0 ? "If the current risks materialize" : "No evidenced downside identified",
      value: Math.max(0, expectedOutcome + downsideDelta),
    },
  ];
}

function getImpactProminence({
  impact,
  majorShare,
  materialShare,
  referenceValue,
}: {
  impact: number;
  majorShare: number;
  materialShare: number;
  referenceValue: number;
}): ForecastDiagnosisDriver["flowProminence"] | null {
  if (impact <= 0 || referenceValue <= 0) return null;
  const share = impact / referenceValue;
  if (share >= majorShare) return "major";
  if (share >= materialShare) return "material";
  return null;
}

const driverProminenceRank: Record<ForecastDiagnosisDriver["flowProminence"], number> = {
  primary: 0,
  major: 1,
  material: 2,
  supporting: 3,
};

function buildDrivers({
  currencyCode,
  dealRows,
  expectedCloseV2Available,
  forecast,
  position,
  usesStageWeightedBaseline,
}: {
  currencyCode: string;
  dealRows: DiagnosisDealRow[];
  expectedCloseV2Available: boolean;
  forecast: ForecastEvaluation;
  position: ReturnType<typeof getStateOfSalesV2ForecastPosition>;
  usesStageWeightedBaseline: boolean;
}) {
  const drivers: ForecastDiagnosisDriver[] = [];
  const evidenceDeals = dealRows
    .filter(deal => deal.evidenceContribution > 0)
    .toSorted((a, b) => b.evidenceContribution - a.evidenceContribution);
  const largestDeal = evidenceDeals.at(0);
  const largestShare = largestDeal && position.expectedFromOpenDeals > 0
    ? largestDeal.evidenceContribution / position.expectedFromOpenDeals
    : 0;
  const topThreeShare = position.expectedFromOpenDeals > 0
    ? evidenceDeals.slice(0, 3).reduce((sum, deal) => sum + deal.evidenceContribution, 0)
      / position.expectedFromOpenDeals
    : 0;

  if (largestDeal) {
    const concentrationStatus = largestShare
      >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.dependency.criticalShareOfForecast
      ? "weak"
      : largestShare >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.dependency.materialShareOfForecast
        ? "watch"
        : "supportive";
    drivers.push({
      dealIds: evidenceDeals.slice(0, 3).map(deal => deal.id),
      explanation: "Shows how much of the forecast depends on the largest Deals.",
      flowProminence: largestShare
        >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.dependency.criticalShareOfForecast
        ? "primary"
        : largestShare >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.dependency.materialShareOfForecast
          ? "major"
          : "supporting",
      flowSummary: concentrationStatus === "supportive"
        ? `${Math.round(largestShare * 100)}% in the largest deal`
        : `${Math.round(largestShare * 100)}% depends on ${largestDeal.name}`,
      headline: concentrationStatus === "supportive"
        ? `The remaining forecast is distributed; the largest deal represents ${Math.round(largestShare * 100)}%.`
        : `${Math.round(largestShare * 100)}% of the remaining forecast depends on ${largestDeal.name}.`,
      id: "revenue-concentration",
      label: "Revenue concentration",
      metrics: [
        { label: "Largest-deal share", value: `${Math.round(largestShare * 100)}%` },
        { label: "Top 3 share", value: `${Math.round(topThreeShare * 100)}%` },
        { label: "Included from largest deal", value: formatMoney(largestDeal.evidenceContribution, currencyCode) },
      ],
      status: concentrationStatus,
    });
  } else {
    drivers.push({
      dealIds: [],
      explanation: "No open Deal value is available for this period.",
      flowProminence: "supporting",
      flowSummary: "Concentration evidence unavailable",
      headline: "Revenue concentration cannot be assessed for these filters.",
      id: "revenue-concentration",
      label: "Revenue concentration",
      metrics: [{ label: "Assessment", value: "Unavailable" }],
      status: "unavailable",
    });
  }

  const movement = forecast.stateOfSalesV2ForecastMovement;
  const weakenedDeals = movement?.available
    ? movement.dealsByCategory.weakened_outlook
    : [];
  const movedOutDeals = movement?.available
    ? movement.dealsByCategory.moved_out
    : [];
  const weakenedImpact = [...weakenedDeals, ...movedOutDeals].reduce(
    (sum, deal) => sum + Math.abs(toFiniteNumber(deal.forecastImpact)),
    0,
  );
  const movementDealsById = new Map<string, DriverDealImpact>();
  for (const deal of [...weakenedDeals, ...movedOutDeals]) {
    const id = String(deal.id);
    const existing = movementDealsById.get(id);
    const impact = Math.abs(toFiniteNumber(deal.forecastImpact));
    const previousContribution = Math.abs(toFiniteNumber(deal.previousForecastContribution));
    movementDealsById.set(id, {
      comparisonValue: Math.max(existing?.comparisonValue ?? 0, previousContribution),
      id,
      impact: (existing?.impact ?? 0) + impact,
    });
  }
  const movementSelection = selectForecastDriverDeals(
    Array.from(movementDealsById.values()),
    Math.max(position.forecast, position.target),
  );
  const movementProminence = getImpactProminence({
    impact: weakenedImpact,
    majorShare: FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.criticalShareOfForecast,
    materialShare: FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.materialShareOfForecast,
    referenceValue: Math.max(position.forecast, position.target),
  });
  if ((weakenedDeals.length || movedOutDeals.length) && movementProminence) {
    const affectedIds = [...weakenedDeals, ...movedOutDeals].map(deal => String(deal.id));
    const affectedCategories = dealRows
      .filter(deal => affectedIds.includes(deal.id))
      .map(deal => deal.forecastCategory);
    const commitSupported = affectedCategories.some(category => category?.toLowerCase().includes("commit"));
    drivers.push({
      allDealIds: movementSelection.allDealIds,
      dealIds: movementSelection.materialDealIds,
      explanation: "These Deals lost forecast support during the last 30 days.",
      flowProminence: movementProminence,
      flowSummary: `${formatMoney(weakenedImpact, currencyCode)} weakened in 30 days`,
      headline: `${formatMoney(weakenedImpact, currencyCode)} of the forecast weakened during the last 30 days.`,
      id: commitSupported ? "commit-weakened" : "forecast-weakened",
      label: commitSupported ? "Commit weakened" : "Forecast weakened",
      metrics: [
        { label: "Relevant Deals", value: String(movementSelection.materialDealIds.length) },
        { label: "Weakened value", value: formatMoney(weakenedImpact, currencyCode) },
        { label: "Evidence window", value: "30 days" },
      ],
      status: movementProminence === "major" ? "weak" : "watch",
    });
  } else if (movement?.available) {
    const affectedIds = [...weakenedDeals, ...movedOutDeals].map(deal => String(deal.id));
    drivers.push({
      allDealIds: movementSelection.allDealIds,
      dealIds: movementSelection.materialDealIds,
      explanation: "No material weakening was found during the last 30 days.",
      flowProminence: "supporting",
      flowSummary: "No material weakening in 30 days",
      headline: "No material Forecast weakening was detected during the last 30 days.",
      id: "forecast-movement",
      label: "Forecast movement",
      metrics: [
        { label: "Observed weakening", value: formatMoney(weakenedImpact, currencyCode) },
        { label: "Affected Deals", value: String(new Set(affectedIds).size) },
        { label: "Assessment", value: "Below reviewed threshold" },
      ],
      status: "supportive",
    });
  } else {
    drivers.push({
      dealIds: [],
      explanation: "A comparable 30-day view is unavailable.",
      flowProminence: "supporting",
      flowSummary: "30-day evidence unavailable",
      headline: "Forecast movement cannot be assessed for these filters.",
      id: "forecast-movement",
      label: "Forecast movement",
      metrics: [{ label: "Assessment", value: "Unavailable" }],
      status: "unavailable",
    });
  }

  if (!expectedCloseV2Available || position.historical || !usesStageWeightedBaseline) {
    const historical = position.historical;
    drivers.push({
      dealIds: [],
      explanation: historical
        ? "Later close dates are not assessed for a closed period."
        : !usesStageWeightedBaseline
          ? "Deal-level close dates are unavailable for this forecast."
          : "Revelate cannot assess whether Deals will close later.",
      flowProminence: "supporting",
      flowSummary: historical ? "Closed period" : "Later close dates unavailable",
      headline: historical
        ? "Later close dates are not evaluated for a closed period."
        : "Deals expected later cannot be assessed for this period.",
      id: "close-timing",
      label: "Close timing",
      metrics: [{ label: "Assessment", value: historical ? "Closed period" : "Unavailable" }],
      status: "unavailable",
    });
  } else {
    const closeTimingDeals = dealRows
      .flatMap(deal => deal.baselineContribution > 0 && deal.closeTiming?.expectedAfterPeriod
        ? [{
          deal,
          gapExposure: Math.max(0, deal.baselineContribution - deal.evidenceContribution),
          laterExpectedValue: deal.fullValue * deal.closeTiming.afterPeriodProbability,
        }]
        : [])
      .toSorted((a, b) => b.gapExposure - a.gapExposure);
    const timingGapExposure = closeTimingDeals.reduce(
      (sum, row) => sum + row.gapExposure,
      0,
    );
    const laterExpectedValue = closeTimingDeals.reduce(
      (sum, row) => sum + row.laterExpectedValue,
      0,
    );
    const timingSelection = selectForecastDriverDeals(
      closeTimingDeals.map(row => ({
        comparisonValue: row.deal.baselineContribution,
        id: row.deal.id,
        impact: row.gapExposure,
      })),
      Math.max(position.forecast, position.target),
    );
    const timingProminence = getImpactProminence({
      impact: timingGapExposure,
      majorShare: FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.criticalShareOfForecast,
      materialShare: FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.materialShareOfForecast,
      referenceValue: Math.max(position.forecast, position.target),
    });
    const affectedDealCount = closeTimingDeals.length;
    const relevantDealCount = timingSelection.materialDealIds.length;
    drivers.push({
      allDealIds: timingSelection.allDealIds,
      dealIds: timingSelection.materialDealIds,
      explanation: affectedDealCount
        ? "Revelate expects these Deals to close after the period."
        : "No material timing risk was found.",
      flowProminence: timingProminence ?? "supporting",
      flowSummary: affectedDealCount
        ? `${formatMoney(laterExpectedValue, currencyCode)} expected after period`
        : "No material Deal value is more likely to close after this period",
      headline: relevantDealCount === 1
        ? "1 Deal is more likely to close after the period."
        : relevantDealCount > 1
          ? `${relevantDealCount} relevant Deals are more likely to close after the period.`
          : "No Deals are currently expected to move beyond the period.",
      id: "close-timing",
      label: "Close timing",
      metrics: affectedDealCount
        ? [
          { label: "Relevant Deals", value: String(relevantDealCount) },
          { label: "Value expected later", value: formatMoney(laterExpectedValue, currencyCode) },
          { label: "Forecast value at risk", value: formatMoney(timingGapExposure, currencyCode) },
        ]
        : [
          { label: "Affected Deals", value: "0" },
          { label: "Assessment", value: "No timing drag" },
        ],
      status: timingProminence === "major"
        ? "weak"
        : timingProminence === "material"
          ? "watch"
          : "supportive",
    });
  }

  const signalRows = buildForecastCurrentSignalEvidence(
    categoryIndicators.forecast.map(risk => ({ risk })),
    forecast.stateOfSalesV2OpenDeals,
    position.expectedFromOpenDeals,
  );
  const addGroupedSignalDriver = ({
    explanation,
    headline,
    id,
    indicators,
    label,
    supportingSummary,
  }: {
    explanation: string;
    headline: (affectedDealCount: number) => string;
    id: string;
    indicators: RiskIndicator[];
    label: string;
    supportingSummary: string;
  }) => {
    if (!usesStageWeightedBaseline) {
      drivers.push({
        dealIds: [],
        explanation: "Deal-level evidence is unavailable for this forecast.",
        flowProminence: "supporting",
        flowSummary: "Deal-level evidence unavailable",
        headline: `${label} cannot be assessed for this forecast.`,
        id,
        label,
        metrics: [{ label: "Assessment", value: "Unavailable for submitted baseline" }],
        status: "unavailable",
      });
      return;
    }
    const groupedRows = signalRows.filter(row => indicators.includes(row.indicator));
    const dealsById = new Map(
      groupedRows.flatMap(row => row.deals).map(deal => [String(deal.id), deal]),
    );
    const diagnosisDealsById = new Map(dealRows.map(deal => [deal.id, deal]));
    const groupedDeals = Array.from(dealsById.keys())
      .flatMap(id => {
        const deal = diagnosisDealsById.get(id);
        if (!deal) return [];
        const gapExposure = Math.max(0, deal.baselineContribution - deal.evidenceContribution);
        return gapExposure > 0 ? [{ deal, gapExposure }] : [];
      })
      .toSorted((a, b) => b.gapExposure - a.gapExposure);
    const gapExposure = groupedDeals.reduce(
      (sum, row) => sum + row.gapExposure,
      0,
    );
    const prominence = getImpactProminence({
      impact: gapExposure,
      majorShare: FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.criticalShareOfForecast,
      materialShare: FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.materialShareOfForecast,
      referenceValue: position.expectedFromOpenDeals,
    });
    const forecastValue = groupedDeals.reduce(
      (sum, row) => sum + row.deal.evidenceContribution,
      0,
    );
    const signalLabels = Array.from(new Set(groupedRows.map(row => row.label)));
    const selection = selectForecastDriverDeals(
      groupedDeals.map(row => ({
        comparisonValue: row.deal.baselineContribution,
        id: row.deal.id,
        impact: row.gapExposure,
      })),
      position.forecast,
    );
    if (!prominence) {
      drivers.push({
        allDealIds: selection.allDealIds,
        dealIds: selection.materialDealIds,
        explanation: `No material ${label.toLowerCase()} risk was found.`,
        flowProminence: "supporting",
        flowSummary: supportingSummary,
        headline: `No material ${label.toLowerCase()} risk was found.`,
        id,
        label,
        metrics: [
          { label: "Affected Deals", value: String(groupedDeals.length) },
          { label: "Forecast value affected", value: formatMoney(gapExposure, currencyCode) },
          { label: "Assessment", value: "Below reviewed threshold" },
        ],
        status: "supportive",
      });
      return;
    }
    const relevantDealCount = selection.materialDealIds.length;
    drivers.push({
      allDealIds: selection.allDealIds,
      dealIds: selection.materialDealIds,
      explanation,
      flowProminence: prominence,
      flowSummary: headline(relevantDealCount),
      headline: headline(relevantDealCount),
      id,
      label,
      metrics: [
        { label: "Relevant Deals", value: String(relevantDealCount) },
        { label: "Forecast value affected", value: formatMoney(gapExposure, currencyCode) },
        { label: "Current forecast value", value: formatMoney(forecastValue, currencyCode) },
        { label: "Main signals", value: signalLabels.slice(0, 3).join(", ") },
      ],
      status: prominence === "major" ? "weak" : "watch",
    });
  };

  addGroupedSignalDriver({
    explanation: "Deals are moving more slowly than the CRM forecast assumes.",
    headline: affectedDealCount => affectedDealCount === 1
      ? "1 Deal is moving too slowly."
      : `${affectedDealCount} Deals are moving too slowly.`,
    id: "timing-and-deal-velocity",
    indicators: TIMING_AND_VELOCITY_INDICATORS,
    label: "Deal progress",
    supportingSummary: "Deals are progressing as expected",
  });

  addGroupedSignalDriver({
    explanation: "Recent activity and next steps are weaker than the CRM forecast assumes.",
    headline: affectedDealCount => affectedDealCount === 1
      ? "1 Deal lacks recent activity."
      : `${affectedDealCount} Deals lack recent activity.`,
    id: "pipeline-momentum",
    indicators: PIPELINE_MOMENTUM_INDICATORS,
    label: "Deal activity",
    supportingSummary: "No material activity gaps",
  });

  return drivers
    .toSorted((a, b) => driverProminenceRank[a.flowProminence] - driverProminenceRank[b.flowProminence]);
}

export function buildForecastDiagnosisStory({
  accumulated,
  chartData,
  companyCurrency,
  crmSubmission,
  expectedCloseV2CompanyForecast,
  filteredPreparedDeals,
  forecast,
  period,
}: ForecastDiagnosisBuildInput): ForecastDiagnosisStory {
  const currencyCode = companyCurrency?.code ?? "SEK";
  const position = getStateOfSalesV2ForecastPosition(forecast, period, accumulated, chartData);
  const fallbackBaseline = getCrmStageWeightedForecast(forecast, period, accumulated);
  const compatibleSubmission = crmSubmission?.scopeMatch === "exact" ? crmSubmission : null;
  const baselineForecast = compatibleSubmission
    ? Math.round(compatibleSubmission.amount)
    : fallbackBaseline;
  const score = getForecastAgreementScore(baselineForecast, position.forecast);
  const selectedMonths = getSelectedPeriodMonths(
    forecast.stateOfSalesV2SelectedPeriodRange.from,
    forecast.stateOfSalesV2SelectedPeriodRange.to,
  );
  const evaluatedOpenDealById = new Map(
    forecast.stateOfSalesV2OpenDeals.map(deal => [String(deal.id), deal]),
  );
  const diagnosisOpenDeals = expectedCloseV2CompanyForecast
    ? filteredPreparedDeals
      .filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost)
      .map(deal => ({
        ...deal,
        ...(evaluatedOpenDealById.get(String(deal.id)) ?? {}),
      })) as DiagnosisOpenDeal[]
    : forecast.stateOfSalesV2OpenDeals as DiagnosisOpenDeal[];
  const evaluatedOpenDeals = applyExpectedCloseV2DealContributions(
    diagnosisOpenDeals,
    expectedCloseV2CompanyForecast,
    selectedMonths,
  );
  const contributingOpenDealCount = new Set(
    evaluatedOpenDeals
      .filter(deal => toFiniteNumber(deal.forecast_contribution) > 0)
      .map(deal => String(deal.id)),
  ).size;
  const dealRows = buildDealRows(
    forecast,
    filteredPreparedDeals,
    evaluatedOpenDeals,
    expectedCloseV2CompanyForecast,
  );
  const weakenedDealIds = getMovementDealIds(forecast, ["weakened_outlook", "moved_out"]);
  const scenarios = buildScenarios(
    position.forecast,
    dealRows,
    weakenedDealIds,
    currencyCode,
    periodConditionLabels[period],
  );
  const downside = scenarios.find(scenario => scenario.kind === "downside")!;
  const upside = scenarios.find(scenario => scenario.kind === "upside")!;
  const drivers = buildDrivers({
    currencyCode,
    dealRows,
    expectedCloseV2Available: Boolean(expectedCloseV2CompanyForecast),
    forecast,
    position,
    usesStageWeightedBaseline: compatibleSubmission === null,
  });
  const difference = position.forecast - baselineForecast;
  const gapBreakdown = buildForecastGapBreakdown(
    dealRows,
    baselineForecast - position.forecast,
    baselineForecast,
    compatibleSubmission === null,
  );
  const remainingTarget = position.target > 0
    ? Math.max(position.target - position.won, 0)
    : null;
  const evidenceWeightedCoverage = remainingTarget === null || position.historical
    ? null
    : remainingTarget === 0
      ? {
        coverageMultiple: null,
        denominatorRemainingTarget: 0,
        eligibleDealCount: contributingOpenDealCount,
        evidenceWeightedPipelineValue: position.expectedFromOpenDeals,
        status: "target_achieved" as const,
      }
      : {
        coverageMultiple: position.expectedFromOpenDeals / remainingTarget,
        denominatorRemainingTarget: remainingTarget,
        eligibleDealCount: contributingOpenDealCount,
        evidenceWeightedPipelineValue: position.expectedFromOpenDeals,
        status: position.expectedFromOpenDeals >= remainingTarget
          ? "covered" as const
          : "gap" as const,
      };

  return {
    baseline: compatibleSubmission
      ? {
        asOf: compatibleSubmission.asOf,
        description: "Latest HubSpot forecast submission matching the period and filters.",
        label: "Submitted forecast",
        source: "hubspot_submission",
      }
      : {
        asOf: null,
        description: "Open pipeline weighted by CRM stage probability, plus revenue already Won.",
        label: "CRM stage-weighted forecast",
        source: "crm_stage_weighted",
      },
    baselineForecast,
    confidence: {
      change: null,
      changePeriod: null,
      label: getForecastAgreementLabel(score),
      score,
    },
    currencyCode,
    dataMode: "live",
    deals: dealRows.map(toPublicDeal),
    drivers,
    expectedOutcome: position.forecast,
    gapBreakdown,
    forecastContext: {
      evidenceWeightedCoverage,
      remainingTarget,
      wonToDate: position.won,
    },
    headline: difference < 0
      ? `The revenue evidence supports ${formatMoney(Math.abs(difference), currencyCode)} less than the CRM baseline.`
      : difference > 0
        ? `The revenue evidence supports ${formatMoney(difference, currencyCode)} more than the CRM baseline.`
        : "The CRM baseline and revenue evidence currently agree.",
    likelyRange: { high: upside.value, low: downside.value },
    period: periodLabels[period],
    periodClosed: period === "prev_quarter",
    scenarios,
    summary: drivers.length
      ? `${drivers[0].headline} The remaining signals explain the difference from CRM.`
      : "No material evidence driver is currently available for these filters.",
    target: position.target > 0 ? position.target : null,
  };
}
